import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button, Input } from '@/components/ui';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import Label from '@/components/Label';
import useCreateCustomCostProvider from '@/hooks/admin/platformCosts/useCreateCustomCostProvider';
import formatPeriodLabel from '../helpers/formatPeriodLabel';
import { ActionsRow, ErrorText, FieldStack, FormStack, MonthHint, SheetBody } from './styled';
import type { AddCustomProviderSheetProps } from './types';

function AddCustomProviderSheet({
    isOpen,
    month,
    onRequestClose,
    onCreated,
}: AddCustomProviderSheetProps) {
    const [displayName, setDisplayName] = useState('');
    const [errorText, setErrorText] = useState<string | null>(null);
    const monthLabel = formatPeriodLabel({ month });

    const { createProvider, isCreatingProvider } = useCreateCustomCostProvider({
        onSuccess: ({ providerId, month: createdMonth }) => {
            toast.success(`${displayName.trim()} added for ${monthLabel}`);
            onCreated({ providerId, month: createdMonth });
            onRequestClose();
        },
        onError: error => {
            setErrorText(error.message || 'Failed to add custom provider');
            toast.error(error.message || 'Failed to add custom provider');
        },
    });

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setDisplayName('');
        setErrorText(null);
    }, [isOpen]);

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            onRequestClose();
        }
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const trimmedName = displayName.trim();
        if (trimmedName.length === 0) {
            setErrorText('Vendor name is required');
            return;
        }
        setErrorText(null);
        createProvider({ displayName: trimmedName, month });
    };

    return (
        <Sheet open={isOpen} onOpenChange={handleOpenChange}>
            <SheetContent side="right" className="w-[520px] max-w-none gap-0 p-0 sm:max-w-none">
                <form onSubmit={handleSubmit} className="flex h-full flex-col">
                    <SheetHeader className="border-b p-6">
                        <SheetTitle>Add custom provider</SheetTitle>
                        <SheetDescription>
                            Creates a manual provider for {monthLabel} only. It will not appear in
                            other months.
                        </SheetDescription>
                    </SheetHeader>

                    <SheetBody>
                        <FormStack>
                            <MonthHint>Month: {monthLabel}</MonthHint>

                            <FieldStack>
                                <Label
                                    name="Vendor name"
                                    htmlFor="custom-provider-name"
                                    required
                                    className="text-sm font-medium text-gray-700"
                                />
                                <Input
                                    id="custom-provider-name"
                                    value={displayName}
                                    onChange={event => setDisplayName(event.target.value)}
                                    placeholder="e.g. Zapier"
                                    autoFocus
                                />
                            </FieldStack>

                            {errorText ? <ErrorText>{errorText}</ErrorText> : null}
                        </FormStack>
                    </SheetBody>

                    <SheetFooter className="border-t p-6">
                        <ActionsRow>
                            <Button type="button" variant="outline" onClick={onRequestClose}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isCreatingProvider}>
                                {isCreatingProvider ? 'Adding…' : 'Add provider'}
                            </Button>
                        </ActionsRow>
                    </SheetFooter>
                </form>
            </SheetContent>
        </Sheet>
    );
}

export default AddCustomProviderSheet;
